<template>
  <div
    :class="[
      'base-tag',
      size,
      {
        'base-tag--selectable': selectable,
        'opacity-50 cursor-not-allowed': disabled,
      },
    ]"
    :style="styles"
    data-testid="base-tag"
  >
    <span
      class="text-truncate text-high-emphasis font-weight-semibold"
      :class="{ 'text-capitalize': capitalize }"
      :title="label"
      data-testid="base-tag-label"
    >
      {{ label }}
    </span>
    <button
      v-if="removeable"
      class="ml-2"
      :class="{
        'pointer-events-none': disabled,
      }"
      data-testid="base-tag-remove"
      :aria-label="t('remove')"
      @click="!disabled && emit('remove')"
    >
      <VIcon
        class="base-tag__close"
        data-testid="base-tag-remove-icon"
        size="x-small"
      >
        close
      </VIcon>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { colorMap, type ColorMap } from '@/types/tags';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

export interface UITag {
  label: string;
  size?: 'small' | 'medium' | 'large';
  color?: keyof ColorMap;
  removeable?: boolean;
  selectable?: boolean;
  capitalize?: boolean;
  disabled?: boolean;
}

const {
  label,
  size = 'small',
  color = '#B8B9B9',
  removeable = false,
  selectable = false,
  capitalize = true,
  disabled = false,
} = defineProps<UITag>();

const styles = computed(() => {
  const shades = colorMap[color] || {
    border: 'var(--default-border, #CCCCCC)',
    background: 'var(--default-background, #F5F5F5)',
  };
  return {
    '--tag-border': shades.border,
    '--tag-background': shades.background,
  };
});

const emit = defineEmits<{
  (e: 'remove'): void;
}>();
</script>

<style lang="scss" scoped>
.base-tag {
  border-radius: 0.25em;
  border: 1px solid var(--tag-border);
  background: var(--tag-background);
  padding: 0.25rem 0.5rem;
  user-select: none;
  display: inline-flex;
  line-height: normal;
  align-items: center;
  overflow: hidden;

  &.small {
    font-size: 0.6875rem;
  }

  &.medium {
    font-size: 0.75rem;
  }

  &.large {
    font-size: 0.875rem;
  }

  &__close {
    cursor: pointer;
    font-size: 0.75rem;
  }

  &--selectable {
    user-select: text;
  }
}
</style>
